import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@components/layout/page-header';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { formatDate } from '@utils/date';
import {
  useGetBillingCycles,
  type BillingCycle
} from '@utils/api/subscriptions/use-get-billing-cycles';

type Row = {
  id: number;
  cycle_start: string;
  cycle_end: string;
  status: string;
  is_locked: string; // yes/no (localized)
  locked_at: string;
};

export const Cycles = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetBillingCycles();

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'id',
        header: t('id'),
        field: 'id',
        maxWidth: 100,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'cycle_start',
        header: t('cycle-start'),
        field: 'cycle_start',
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'cycle_end',
        header: t('cycle-end'),
        field: 'cycle_end',
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'status',
        header: t('status'),
        field: 'status',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'is_locked',
        header: t('is-locked'),
        field: 'is_locked',
        minWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: t('yes') },
          { label: t('no'), value: t('no') }
        ],
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'locked_at',
        header: t('locked-at'),
        field: 'locked_at',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date',
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t]
  );

  const rows = useMemo<Row[]>(() => {
    if (!data?.result) return [];
    return data.result.map((item: BillingCycle) => ({
      id: item.id,
      cycle_start: item.cycle_start ? formatDate(item.cycle_start) : '',
      cycle_end: item.cycle_end ? formatDate(item.cycle_end) : '',
      status: item.status,
      is_locked: item.is_locked ? t('yes') : t('no'),
      locked_at: item.locked_at ? formatDate(item.locked_at) : ''
    }));
  }, [data, t]);

  return (
    <div className="px-4">
      <PageHeader
        title={t('cycles')}
        caption={t('a-list-of-all-the-billing-cycles-and-cycle-details')}
      />
      <div className="sm:px-6 lg:px-8">
        <div className="flow-root">
          <div className="sm:-mx-6 lg:-mx-8">
            <div className="flow-root">
              <div className="py-2 p-2 md:p-0 min-w-full">
                <DataGridV2<Row>
                  data={rows}
                  columns={columns}
                  enableRowSelection={false}
                  enablePagination
                  manualPagination
                  enableSorting
                  manualSorting
                  enableFiltering
                  manualFiltering
                  totalItems={data?.total_results ?? rows.length}
                  pageSize={25}
                  pageSizeOptions={[25, 50, 75, 100]}
                  showColumnFilters={false}
                  filterMode="popover"
                  height="77vh"
                  dense
                  grid={false}
                  striped
                  loading={isLoading || !data}
                  getRowId={row => String(row.id)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
